/**
 * Thin API client.
 *
 * The access token is held in memory only — never localStorage — so a stolen
 * XSS payload can't lift a long-lived credential. The refresh token lives in an
 * httpOnly cookie that JS cannot read, which is why every request sends
 * credentials.
 *
 * STAYING SIGNED IN (owner, 2026-09-23: "i keep having to log in, it signs me
 * out frequently, i do not want to be logged out"). What production showed and
 * what each piece below answers:
 *  - refreshes racing — two tabs waking together sent the same cookie twice:
 *    `refreshSession` is single-flight inside a tab and holds a Web Lock across
 *    tabs (the server now also forgives the race — see `AuthService.refresh`);
 *  - a phone slept mid-refresh and the reply never came, leaving a request that
 *    never settles: the refresh has a deadline, and a timed-out or offline
 *    refresh is "try again", never "signed out";
 *  - a screen woken after 15 minutes fired its polls into an expired token all
 *    at once: `startSessionKeepAlive` renews shortly before expiry and again on
 *    wake / focus / reconnect;
 *  - after a real sign-out the screen kept polling, hammering the refresh route
 *    every few seconds: the server's refusal now reaches the app through
 *    `onSessionEnded`, which shows the sign-in page once and stops.
 *
 * SUSPENDED (EclipseGG platform, 2026-10-19: "warn, then suspend"). A venue the
 * seller has suspended answers 403 `venue_suspended` on every staff route and
 * on refresh. That is not a sign-out — the server keeps the cookie on purpose,
 * so reactivating resumes every till without anyone typing a password — so it
 * reaches the app through `onVenueSuspended`, and only a 401 is ever
 * `isSessionRejected`. Each renewal is announced as well (`onSessionRenewed`):
 * the app re-reads `/auth/me` then, which is how the subscription banner
 * counts down on a screen left open for days.
 *
 * ONE TILL, ONE SESSION AT A TIME (EclipseGG platform 2 review, 2026-10-20). A
 * till is shared: the owner signs out and a cashier at another venue signs in,
 * while an answer from the owner's session is still out — a renewal or Try
 * again's refresh held by a flapping uplink, a poll. Answered after the
 * cashier's sign-in, a refresh put the owner's token on the cashier's screen,
 * and its renewal had the app re-read `/auth/me` with it: the owner's name and
 * keys on the cashier's screen. A late 403 parked the cashier on the suspended
 * screen; a late 401 signed them out. So this module counts sessions
 * (`startNewSession`, at every sign-in and every sign-out), every request takes
 * the count as it is sent, and an answer that comes back to a later session is
 * nobody's: a refresh adopts no token and announces nothing (it settles to
 * `SessionChangedError`), a 401 is neither refreshed nor replayed, a refused
 * refresh signs nobody out, and a 403 `venue_suspended` parks nothing. Only
 * the browser's own cookie jar is beyond it: a late answer's Set-Cookie still
 * lands, so the most it can cost the next person is a sign-in at their next
 * renewal, never somebody else's session.
 */

import { VENUE_SUSPENDED_CODE } from '@gc/shared';

const BASE_URL = '/api';

let accessToken: string | null = null;
/** When the current access token should be renewed, on THIS device's clock. */
let renewAt: number | null = null;
/**
 * The server has refused this browser's refresh cookie. Until someone signs in
 * again there is nothing to refresh, so a 401 is passed straight through
 * instead of costing another doomed round trip.
 */
let sessionEnded = false;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  renewAt = token ? renewalTime(token, Date.now()) : null;
  if (token) sessionEnded = false;
  scheduleRenewal();
}

export function getAccessToken(): string | null {
  return accessToken;
}

/* -------------------------------------------------------------------------- */
/* Whose session                                                              */
/* -------------------------------------------------------------------------- */

/** How many sessions this tab has started; see the header and `startNewSession`. */
let sessionCount = 0;

/**
 * Somebody signs in or out on this tab: call it as a sign-in's token is
 * adopted, and as Sign out is pressed. From here every answer still out from
 * the session before is nobody's (see the header).
 */
export function startNewSession(): void {
  sessionCount += 1;
}

/**
 * The session this tab is on now. For the few callers that build their own
 * `fetch`: take it before sending, and hand it to `announceIfVenueSuspended`
 * with the answer.
 */
export function currentSession(): number {
  return sessionCount;
}

/**
 * What a refresh (or a session replacement) settles to when its answer comes
 * back to a later session on this tab than the one it was asked for: its token
 * is not adopted, and nothing is announced. Deliberately not an `ApiError`: it
 * says nothing about the server or about the session now on screen, so nothing
 * that reads a 401 as a sign-out, or a 403 as a suspension, can read it as
 * either.
 */
export class SessionChangedError extends Error {
  constructor() {
    super('This answer belongs to a session that has ended on this screen.');
    this.name = 'SessionChangedError';
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The one answer that means "this browser is signed out": a 401. Anything else
 * — offline, a timeout, a 502 while the server restarts for a deploy, a
 * suspended venue's 403 — says nothing about the session, and must never cost
 * anyone their sign-in.
 */
export function isSessionRejected(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * The venue's EclipseGG service is suspended: 403 `venue_suspended`, from any
 * staff route, from refresh, and from sign-in. Never a sign-out (above).
 */
export function isVenueSuspended(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.code === VENUE_SUSPENDED_CODE;
}

/* -------------------------------------------------------------------------- */
/* Session-ended notification                                                 */
/* -------------------------------------------------------------------------- */

const sessionEndedListeners = new Set<() => void>();

/** Called once when the server refuses this browser's session. Returns an unsubscribe. */
export function onSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

function endSession(): void {
  setAccessToken(null);
  sessionEnded = true;
  for (const listener of [...sessionEndedListeners]) listener();
}

/* -------------------------------------------------------------------------- */
/* Venue-suspended and session-renewed notifications                          */
/* -------------------------------------------------------------------------- */

const venueSuspendedListeners = new Set<() => void>();

/**
 * Called on every 403 `venue_suspended` answer — once per answer, whichever
 * request met it — except sign-in's, which the form words itself, and one
 * that comes back to a later session than it was sent in (see the header).
 * Nothing is cleared: the token and the cookie are exactly what reactivation
 * resumes. Returns an unsubscribe.
 */
export function onVenueSuspended(listener: () => void): () => void {
  venueSuspendedListeners.add(listener);
  return () => {
    venueSuspendedListeners.delete(listener);
  };
}

function announceVenueSuspended(): void {
  for (const listener of [...venueSuspendedListeners]) listener();
}

/**
 * Announce `error` if it is a 403 `venue_suspended`, but only while the
 * session it was sent in (`sentIn`, `currentSession()` as it left) is still
 * this tab's: a late refusal from the session before parks nobody. `request()`
 * goes through here, and so do the few callers that build their own `fetch`
 * (the report export, the media upload), so their own suspended answer parks
 * the app too.
 */
export function announceIfVenueSuspended(error: unknown, sentIn: number): void {
  if (sentIn === sessionCount && isVenueSuspended(error)) announceVenueSuspended();
}

const sessionRenewedListeners = new Set<() => void>();

/**
 * Called after each refresh that minted a new access token: the keep-alive's
 * renewals, a wake, a 401 refreshed and replayed. Callers that share one
 * refresh share one call; a sign-in adopts its token directly and is not
 * announced. Returns an unsubscribe.
 */
export function onSessionRenewed(listener: () => void): () => void {
  sessionRenewedListeners.add(listener);
  return () => {
    sessionRenewedListeners.delete(listener);
  };
}

/* -------------------------------------------------------------------------- */
/* Refresh                                                                    */
/* -------------------------------------------------------------------------- */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Internal: prevents infinite recursion when the refresh call itself 401s. */
  skipRefresh?: boolean;
}

/**
 * The refresh currently in flight, shared by every caller.
 *
 * The server ROTATES the refresh cookie each time it is used, so a second
 * concurrent refresh presents an already-spent token and is rejected. Without
 * this, any two requests that 401 together — routine, since the Dashboard polls
 * on a timer while other screens fetch — would both try to refresh, one would
 * win, and the loser's `catch` would null the access token and drop the user
 * back to the login screen holding a perfectly valid session.
 *
 * React StrictMode makes it reproduce every single time in dev by running the
 * boot effect twice, which is how it was found.
 *
 * Shared within one session only (`session`, the count it was asked in): one
 * still out from the session before belongs to somebody else, adopts nothing
 * when it lands, and must not be what the next person's callers wait on.
 */
let inFlightRefresh: { session: number; promise: Promise<string> } | null = null;

/**
 * The session replacement in flight, if any (see `replaceSession`), and the
 * session it was asked in. `settled` resolves to the token it adopted, or null
 * when it failed; it never rejects, because it is only ever waited on.
 */
let sessionReplacement: { session: number; settled: Promise<string | null> } | null = null;

/** Cross-tab lock name: tabs share one cookie, so they take turns rotating it. */
const REFRESH_LOCK = 'gc-staff-refresh';

/**
 * A refresh that has not answered in this long is abandoned and retried. A
 * phone that sleeps mid-request can leave the fetch pending indefinitely, and
 * every other request waits on this promise — without a deadline the whole app
 * froze until someone reloaded it.
 */
const REFRESH_DEADLINE_MS = 20_000;

/**
 * Mint a new access token, coalescing concurrent callers onto one request.
 * Use this rather than posting to `/auth/refresh` directly.
 *
 * While a password change is replacing the session, a refresh would present
 * the very cookie that change is retiring; it waits for the change instead,
 * and takes the change's new token when there is one (see `replaceSession`).
 */
export function refreshSession(): Promise<string> {
  const asked = sessionCount;
  if (inFlightRefresh?.session === asked) return inFlightRefresh.promise;

  const mint = () => mintAccessToken(asked);
  // Only this session's replacement: an older one's token is somebody else's.
  const replacing = sessionReplacement?.session === asked ? sessionReplacement.settled : null;
  const flight = {
    session: asked,
    promise: (replacing
      ? replacing.then((adopted) => adopted ?? withRefreshLock(mint))
      : withRefreshLock(mint)
    ).finally(() => {
      if (inFlightRefresh === flight) inFlightRefresh = null;
    }),
  };
  inFlightRefresh = flight;

  return flight.promise;
}

/**
 * One refresh round trip for the session it was asked in (`asked`): adopt the
 * new token, or announce a refused session. Neither once another session has
 * started on this tab: whatever the answer, it settles to
 * `SessionChangedError` (see the header). Checked before sending as well, for
 * a refresh that waited on another tab's lock or on a password change while
 * somebody signed out: it would only present the next person's cookie on the
 * previous person's behalf.
 */
async function mintAccessToken(asked: number): Promise<string> {
  if (asked !== sessionCount) throw new SessionChangedError();
  let minted: string;
  try {
    ({ accessToken: minted } = await request<{ accessToken: string }>('/auth/refresh', {
      method: 'POST',
      skipRefresh: true,
      signal: refreshDeadline(),
    }));
  } catch (error) {
    // A refusal that outlived its session ends nobody's: the person signed in
    // now has a cookie of their own.
    if (asked !== sessionCount) throw new SessionChangedError();
    if (isSessionRejected(error)) endSession();
    throw error;
  }
  if (asked !== sessionCount) throw new SessionChangedError();
  setAccessToken(minted);
  for (const listener of [...sessionRenewedListeners]) listener();
  return minted;
}

/**
 * POST to a route whose answer REPLACES this browser's session — a new
 * refresh cookie and a new access token, with every older one refused from
 * the moment the server saves. Today that is changing your own password.
 *
 * Until the answer lands, this tab still holds the old token and the old
 * cookie. A request that met a 401 in that window (the top bar's minute pulse,
 * a due renewal) refreshed with the old cookie, was told `session_ended`, and
 * signed out the very person who had just proved their password. So while it
 * runs: this tab's refreshes wait for it and take its token; other tabs wait
 * on the shared refresh lock; and a refresh already under way finishes first,
 * so its answer cannot land a retired cookie on top of the new one.
 *
 * Resolves to the new access token, already adopted — within the session it
 * was asked in only. Once somebody signs out (or in) it is never sent, and an
 * answer that lands after that adopts nothing: `SessionChangedError`, as for
 * a refresh (see the header).
 */
export function replaceSession(path: string, body: unknown): Promise<string> {
  const asked = sessionCount;
  // Any session's: whoever it belongs to, its answer sets the cookie, so it
  // finishes first.
  const earlier = inFlightRefresh?.promise;

  const replacement = (async () => {
    if (earlier) await earlier.catch(() => undefined);
    return withRefreshLock(async () => {
      // The POST cannot refresh-and-replay its own 401 — every refresh is
      // waiting on it — so it must not set out with a token about to lapse.
      if (!accessToken || (renewAt !== null && Date.now() >= renewAt)) await mintAccessToken(asked);
      // Never sent on the next person's token.
      if (asked !== sessionCount) throw new SessionChangedError();
      const { accessToken: minted } = await request<{ accessToken: string }>(path, {
        method: 'POST',
        body,
        skipRefresh: true,
        // Every refresh in the tab is waiting on this; a phone asleep
        // mid-request must not hold them all for good. The caller reports a
        // timeout as "not confirmed", never as "not changed".
        signal: deadline(REFRESH_DEADLINE_MS),
      });
      if (asked !== sessionCount) throw new SessionChangedError();
      setAccessToken(minted);
      return minted;
    });
  })();

  const settled = replacement.then(
    (minted) => minted,
    () => null,
  );
  const entry = { session: asked, settled };
  sessionReplacement = entry;
  void settled.then(() => {
    if (sessionReplacement === entry) sessionReplacement = null;
  });

  return replacement;
}

/**
 * The longest a tab waits for another tab's refresh before going ahead alone.
 * iOS suspends background tabs outright, so a tab frozen halfway through its
 * refresh can sit on the lock until someone switches back to it; the screen in
 * the owner's hand must not wait on that.
 */
const LOCK_WAIT_MS = 5_000;

/**
 * Runs the refresh under a Web Lock shared by every tab of this origin, so one
 * tab never presents the cookie another tab is in the middle of rotating. Where
 * the browser has no Web Locks (older Safari), or the lock is not granted in
 * time, it simply runs — the server's grace window covers that race too.
 */
async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks || typeof locks.request !== 'function') return task();

  let started = false;
  const signal = deadline(LOCK_WAIT_MS);
  try {
    return (await locks.request(REFRESH_LOCK, signal ? { signal } : {}, () => {
      started = true;
      return task();
    })) as T;
  } catch (error) {
    // Only the WAIT gave up (a timeout signal rejects with TimeoutError, a plain
    // abort with AbortError); a failure of the refresh itself is its own answer.
    const name = (error as { name?: unknown } | null)?.name;
    if (!started && (name === 'TimeoutError' || name === 'AbortError')) return task();
    throw error;
  }
}

/**
 * A signal that gives up after `ms`, for a request that must not hang for
 * good: a phone asleep mid-request can leave a fetch pending indefinitely.
 * Undefined where the browser has no `AbortSignal.timeout` (older Safari),
 * which then simply waits, as every request did before.
 */
export function deadline(ms: number): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(ms)
    : undefined;
}

function refreshDeadline(): AbortSignal | undefined {
  return deadline(REFRESH_DEADLINE_MS);
}

/* -------------------------------------------------------------------------- */
/* Keep-alive                                                                 */
/* -------------------------------------------------------------------------- */

/** Renew no later than this long before the access token runs out. */
const RENEW_BEFORE_EXPIRY_MS = 60_000;
/** After a failed renewal that was NOT a sign-out (offline, 502), try again this soon. */
const RENEW_RETRY_MS = 30_000;
/** Never schedule tighter than this, whatever a token claims. */
const MIN_RENEW_DELAY_MS = 5_000;

let renewalTimer: ReturnType<typeof setTimeout> | null = null;
let keepAliveActive = false;

/**
 * When to renew a token, measured on this device's clock from the moment it
 * arrived. The token's own lifetime (`exp - iat`, both SERVER time) is used
 * rather than `exp` itself, so a phone whose clock is minutes off still renews
 * on time. Null for anything that is not a readable JWT.
 */
export function renewalTime(token: string, receivedAt: number): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number' || typeof payload.iat !== 'number') return null;
  const lifetimeMs = (payload.exp - payload.iat) * 1000;
  if (!(lifetimeMs > 0)) return null;
  const margin = Math.min(RENEW_BEFORE_EXPIRY_MS, lifetimeMs * 0.2);
  return receivedAt + lifetimeMs - margin;
}

function decodeJwtPayload(token: string): { exp?: unknown; iat?: unknown } | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object'
      ? (parsed as { exp?: unknown; iat?: unknown })
      : null;
  } catch {
    return null;
  }
}

function clearRenewalTimer(): void {
  if (renewalTimer !== null) clearTimeout(renewalTimer);
  renewalTimer = null;
}

function scheduleRenewal(delayOverrideMs?: number): void {
  clearRenewalTimer();
  if (!keepAliveActive) return;
  const delay =
    delayOverrideMs ??
    (renewAt === null ? null : Math.max(MIN_RENEW_DELAY_MS, renewAt - Date.now()));
  if (delay === null) return;
  renewalTimer = setTimeout(() => void renewIfDue(), delay);
}

function renewalDue(now: number): boolean {
  if (sessionEnded) return false;
  // Signed in but holding no token — a renewal failed while offline.
  if (!accessToken) return true;
  return renewAt !== null && now >= renewAt;
}

/**
 * Renew the access token if it is due (or missing). Safe to call as often as
 * the browser likes: a fresh token makes it a no-op, and concurrent callers
 * share one refresh.
 */
export async function renewIfDue(now: number = Date.now()): Promise<void> {
  if (!keepAliveActive || !renewalDue(now)) return;
  const asked = sessionCount;
  try {
    await refreshSession();
  } catch (error) {
    // A renewal for a session that has since ended on this tab: the next one
    // keeps its own timer, which a retry scheduled here would replace.
    if (asked !== sessionCount) return;
    // A sign-out has already been announced by `refreshSession`; anything else
    // is the network, so try again shortly rather than give up on the session.
    if (!isSessionRejected(error)) scheduleRenewal(RENEW_RETRY_MS);
  }
}

/**
 * Keep the signed-in session warm for as long as the app is open: renew just
 * before the access token expires, and again whenever the page comes back —
 * a phone unlocked, a tab re-focused, the network returning — because a
 * backgrounded page's timers do not run. Returns the stop function.
 */
export function startSessionKeepAlive(): () => void {
  keepAliveActive = true;
  scheduleRenewal();

  const onWake = () => void renewIfDue();
  const onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') onWake();
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    window.addEventListener('pageshow', onWake);
  }

  return () => {
    keepAliveActive = false;
    clearRenewalTimer();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
      window.removeEventListener('pageshow', onWake);
    }
  };
}

/**
 * An access token that is good right now, renewing first if it is due. For
 * the few callers that build their own `fetch` (multipart upload, file
 * downloads) and so cannot use the 401-refresh-and-replay below. Null when
 * there is none — or when somebody signed out (or in) while it renewed: the
 * token held then is the next person's, never the caller's.
 */
export async function freshAccessToken(): Promise<string | null> {
  const asked = sessionCount;
  if (!sessionEnded && (!accessToken || (renewAt !== null && Date.now() >= renewAt))) {
    try {
      await refreshSession();
    } catch {
      // Fall through with whatever we hold; the caller's request reports it.
    }
  }
  return asked === sessionCount ? accessToken : null;
}

/* -------------------------------------------------------------------------- */
/* Requests                                                                   */
/* -------------------------------------------------------------------------- */

async function parseError(response: Response): Promise<ApiError> {
  let code = 'unknown_error';
  let message = response.statusText || 'Request failed';
  let details: unknown;

  try {
    const payload = (await response.json()) as {
      code?: string;
      message?: string;
      details?: unknown;
    };
    if (payload.code) code = payload.code;
    if (payload.message) message = payload.message;
    details = payload.details;
  } catch {
    // Non-JSON error body; keep the status text.
  }

  return new ApiError(response.status, code, message, details);
}

/**
 * Routes whose 401 is an ANSWER, not an expired token: a wrong password at
 * sign-in, or the refresh itself being refused. Refreshing on those would be
 * meaningless at best — at sign-in it announced a sign-out to a page that was
 * not signed in yet.
 */
const NO_REFRESH_PATHS = new Set(['/auth/refresh', '/auth/login']);

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, skipRefresh = false } = options;
  // The session this request is sent for. Its answer is that session's,
  // whatever has happened on the tab by the time it lands (see the header).
  const sentIn = sessionCount;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    // Sends the httpOnly refresh cookie.
    credentials: 'include',
    signal,
  });

  // Access token expired: mint a new one once, then replay the original request.
  // Concurrent 401s share a single refresh — see `refreshSession`.
  if (response.status === 401 && !skipRefresh && !sessionEnded && !NO_REFRESH_PATHS.has(path)) {
    // Sent before somebody signed out (or in): its 401 is about a token
    // nobody holds now. A refresh would renew the next person's session on
    // its behalf, and the replay would carry their token, so it is reported
    // as it is — here, and wherever the session changes while it waits.
    if (sentIn !== sessionCount) throw await parseError(response);
    try {
      await refreshSession();
    } catch (refreshError) {
      if (sentIn !== sessionCount) throw await parseError(response);
      // Signed out: `refreshSession` has already announced it; report this
      // request's own 401. Offline, a timeout, a deploy restart: the session
      // is fine, so surface THAT failure — a 401 here would read as a sign-out
      // to any caller that checks.
      if (!isSessionRejected(refreshError)) throw refreshError;
      setAccessToken(null);
      throw await parseError(response);
    }

    if (sentIn !== sessionCount) throw await parseError(response);
    try {
      return await request<T>(path, { ...options, skipRefresh: true });
    } catch (replayError) {
      // A freshly minted token refused outright: the account itself is the
      // problem (deactivated, password changed). Report the original 401 —
      // and drop the token only if it is still this session's.
      if (isSessionRejected(replayError)) {
        if (sentIn === sessionCount) setAccessToken(null);
        throw await parseError(response);
      }
      // Anything else is the replay's own honest answer — a 400 on a field,
      // a 409 — and must reach the form rather than be disguised as a 401.
      throw replayError;
    }
  }

  if (!response.ok) {
    const failure = await parseError(response);
    // Whichever request meets a suspended venue first parks the app on the
    // suspended screen; a burst of polls is a burst of the same news, which
    // the listener absorbs. Never sign-in's: the form words that refusal
    // itself (`auth.venueSuspended`), and there is no session to park. Nor
    // one sent before somebody signed out (or in): it would park the next
    // person on a screen about another venue.
    if (path !== '/auth/login') announceIfVenueSuspended(failure, sentIn);
    throw failure;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
